import React, { useState } from 'react';
import { Image, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { useShop } from '../../context/ShopContext';
import fileUploadService from '../../services/fileUploadService';
import { getProductImageUrl } from '../../utils/imageUtils';

const categories = ['COFFEE', 'TEA', 'SNACKS', 'DESSERTS', 'BEVERAGES'];
const sizes = ['SMALL', 'MEDIUM', 'LARGE'];

const predefinedImages = [
  { value: 'espresso.jpg', label: 'Espresso', category: 'COFFEE' },
  { value: 'americano.jpg', label: 'Americano', category: 'COFFEE' },
  { value: 'cappuccino.jpg', label: 'Cappuccino', category: 'COFFEE' },
  { value: 'latte.jpg', label: 'Latte', category: 'COFFEE' },
  { value: 'mocha.jpg', label: 'Mocha', category: 'COFFEE' },
  { value: 'macchiato.jpg', label: 'Macchiato', category: 'COFFEE' },
  { value: 'flat-white.jpg', label: 'Flat White', category: 'COFFEE' },
  { value: 'iced-latte.jpg', label: 'Iced Latte', category: 'COFFEE' },
  { value: 'iced-americano.jpg', label: 'Iced Americano', category: 'COFFEE' },
  { value: 'iced-mocha.jpg', label: 'Iced Mocha', category: 'COFFEE' },
  { value: 'frappuccino.jpg', label: 'Frappuccino', category: 'COFFEE' },
  { value: 'cold-brew.jpg', label: 'Cold Brew', category: 'COFFEE' },
  { value: 'tea-black.jpg', label: 'Black Tea', category: 'TEA' },
  { value: 'tea-green.jpg', label: 'Green Tea', category: 'TEA' },
  { value: 'tea-herbal.jpg', label: 'Herbal Tea', category: 'TEA' },
  { value: 'iced-tea.jpg', label: 'Iced Tea', category: 'TEA' },
  { value: 'croissant.jpg', label: 'Croissant', category: 'SNACKS' },
  { value: 'muffin-blueberry.jpg', label: 'Blueberry Muffin', category: 'SNACKS' },
  { value: 'muffin-chocolate.jpg', label: 'Chocolate Muffin', category: 'SNACKS' },
  { value: 'cookie-chocolate.jpg', label: 'Chocolate Cookie', category: 'SNACKS' },
  { value: 'cookie-oatmeal.jpg', label: 'Oatmeal Cookie', category: 'SNACKS' },
  { value: 'sandwich-ham.jpg', label: 'Ham Sandwich', category: 'SNACKS' },
  { value: 'sandwich-veggie.jpg', label: 'Veggie Sandwich', category: 'SNACKS' },
  { value: 'bagel.jpg', label: 'Bagel', category: 'SNACKS' },
  { value: 'toast.jpg', label: 'Toast', category: 'SNACKS' },
  { value: 'brownie.jpg', label: 'Brownie', category: 'DESSERTS' },
  { value: 'cake-chocolate.jpg', label: 'Chocolate Cake', category: 'DESSERTS' },
  { value: 'cake-cheesecake.jpg', label: 'Cheesecake', category: 'DESSERTS' },
  { value: 'hot-chocolate.jpg', label: 'Hot Chocolate', category: 'BEVERAGES' },
  { value: 'lemonade.jpg', label: 'Lemonade', category: 'BEVERAGES' },
  { value: 'smoothie-berry.jpg', label: 'Berry Smoothie', category: 'BEVERAGES' },
  { value: 'smoothie-banana.jpg', label: 'Banana Smoothie', category: 'BEVERAGES' },
  { value: 'water.jpg', label: 'Water', category: 'BEVERAGES' },
  { value: 'soda-cola.jpg', label: 'Cola', category: 'BEVERAGES' },
  { value: 'soda-sprite.jpg', label: 'Sprite', category: 'BEVERAGES' },
  { value: 'juice-orange.jpg', label: 'Orange Juice', category: 'BEVERAGES' },
  { value: 'juice-apple.jpg', label: 'Apple Juice', category: 'BEVERAGES' }
];

const MenuTab = ({ createProduct, t, setShowMenuCustomization, showMenuCustomization, MenuCustomization }) => {
  const { products } = useShop();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name:'', description:'', price:'', category:'COFFEE', size:'MEDIUM', imageUrl:'' });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageSource, setImageSource] = useState('predefined');

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = fileUploadService.validateFile(file);
      if (!validation.valid) { toast.error(validation.error); return; }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return;
    setUploadingImage(true);
    try {
      const result = await fileUploadService.uploadProductImage(selectedImage);
      if (result.success) {
        setNewProduct(prev => ({ ...prev, imageUrl: result.data.filename }));
        toast.success(t('dashboard.imageUploadedSuccessfully'));
      } else {
        toast.error(result.error);
      }
    } catch {
      toast.error(t('dashboard.uploadFailed'));
    } finally { setUploadingImage(false); }
  };

  const handlePredefinedImageSelect = (filename) => {
    setNewProduct(p => ({ ...p, imageUrl: filename }));
    setImagePreview(getProductImageUrl(filename));
  };

  const getFilteredImages = () => predefinedImages.filter(img => img.category === newProduct.category);

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (selectedImage && !newProduct.imageUrl) await handleImageUpload();
    const result = await createProduct(newProduct);
    if (result.success) {
      setShowAddProduct(false);
      setNewProduct({ name:'', description:'', price:'', category:'COFFEE', size:'MEDIUM', imageUrl:'' });
      setSelectedImage(null); setImagePreview(null); setImageSource('predefined');
      toast.success(t('dashboard.productAddedSuccessfully'));
    } else {
      if (result.code === 'SUBSCRIPTION_REQUIRED') {
        toast.error(t('subscription.expired.title','Subscription expired'));
      } else {
        toast.error(result.error || t('dashboard.failedToAddProduct'));
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">{t('dashboard.menuManagement')}</h2>
        <div className="flex items-center space-x-3">
          <button onClick={() => setShowMenuCustomization(true)} className="btn-secondary">{t('dashboard.customizeMenu')}</button>
          <button onClick={() => setShowAddProduct(true)} className="btn-primary">{t('dashboard.addProduct')}</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', ...categories].map(cat => {
          const count = cat === 'all' ? (products?.length || 0) : (products?.filter(p => p.category === cat).length || 0);
          return (
            <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === cat ? 'bg-[#a15507] text-white' : 'bg-[#1f2937]/50 text-gray-300 hover:text-white'}`}>{cat === 'all' ? t('dashboard.allCategories') : cat}<span className="ml-1 text-xs opacity-75">({count})</span></button>
          );
        })}
      </div>
      {showAddProduct && (
        <div className="card mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">{t('dashboard.addNewProduct')}</h3>
          <form onSubmit={handleAddProduct} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder={t('dashboard.productName')} value={newProduct.name} onChange={e => setNewProduct({...newProduct, name: e.target.value})} className="input-field" required />
            <input type="number" step="0.01" placeholder={t('dashboard.price')} value={newProduct.price} onChange={e => setNewProduct({...newProduct, price: e.target.value})} className="input-field" required />
            <select value={newProduct.category} onChange={e => { const c = e.target.value; setNewProduct({...newProduct, category: c, imageUrl:''}); setImagePreview(null); }} className="input-field">{categories.map(c => <option key={c}>{c}</option>)}</select>
            <select value={newProduct.size} onChange={e => setNewProduct({...newProduct, size: e.target.value})} className="input-field">{sizes.map(s => <option key={s}>{s}</option>)}</select>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('dashboard.productImage')}</label>
              <div className="flex space-x-2 mb-4">
                <button type="button" onClick={() => setImageSource('predefined')} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${imageSource === 'predefined' ? 'bg-[#a15507] text-white' : 'bg-[#1f2937]/50 text-gray-300 hover:text-white'}`}>{t('dashboard.chooseFromLibrary')}</button>
                <button type="button" onClick={() => setImageSource('upload')} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${imageSource === 'upload' ? 'bg-[#a15507] text-white' : 'bg-[#1f2937]/50 text-gray-300 hover:text-white'}`}>{t('dashboard.uploadCustomImage')}</button>
              </div>
              {imageSource === 'predefined' && (
                <div className="space-y-4">
                  {getFilteredImages().length > 0 ? (
                    <>
                      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-80 overflow-y-auto">
                        {getFilteredImages().map(img => (
                          <div key={img.value} onClick={() => handlePredefinedImageSelect(img.value)} className={`aspect-square rounded-lg cursor-pointer border-2 transition-all ${newProduct.imageUrl === img.value ? 'border-[#a15507] scale-105' : 'border-gray-600 hover:border-gray-400'}`}>
                            <img src={getProductImageUrl(img.value)} alt={img.label} className="w-full h-full object-cover rounded-lg" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                            <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-lg" style={{ display: 'none' }}>
                              <Image className="w-6 h-6 text-gray-600" />
                            </div>
                          </div>
                        ))}
                      </div>
                      {newProduct.imageUrl && <p className="text-green-400 text-sm">{newProduct.imageUrl}</p>}
                    </>
                  ) : (
                    <div className="text-center py-16 text-gray-400 min-h-80 flex flex-col items-center justify-center">
                      <Image className="w-12 h-12 mx-auto mb-2" />
                      <p>{t('dashboard.noImagesAvailableForCategory')}: {newProduct.category}</p>
                      <p className="text-sm">{t('dashboard.tryUploadingCustomImage')}</p>
                    </div>
                  )}
                </div>
              )}
              {imageSource === 'upload' && (
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" id="image-upload" />
                    <label htmlFor="image-upload" className="flex items-center justify-center w-full h-80 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-[#a15507] transition-colors">
                      {imagePreview ? <img src={imagePreview} alt={t('dashboard.preview')} className="w-full h-full object-contain rounded-lg" /> : (
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-400 text-sm">{t('dashboard.clickToUploadImage')}</p>
                          <p className="text-gray-500 text-xs">{t('dashboard.jpgPngUpTo10mb')}</p>
                        </div>
                      )}
                    </label>
                  </div>
                  {selectedImage && (
                    <button type="button" onClick={handleImageUpload} disabled={uploadingImage} className="btn-upload">
                      {uploadingImage ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>{t('dashboard.uploading')}</span></> : <><Upload className="w-4 h-4" /><span>{t('dashboard.upload')}</span></>}
                    </button>
                  )}
                </div>
              )}
            </div>
            <textarea placeholder={t('dashboard.description')} value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} className="input-field" rows="3" />
            <div className="flex space-x-2">
              <button type="submit" className="btn-primary">{t('dashboard.addProductButton')}</button>
              <button type="button" onClick={() => setShowAddProduct(false)} className="btn-secondary">{t('dashboard.cancelButton')}</button>
            </div>
          </form>
        </div>
      )}
      {products && products.filter(p => selectedCategory === 'all' || p.category === selectedCategory).length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.filter(p => selectedCategory === 'all' || p.category === selectedCategory).map(product => (
            <div key={product.id} className="product-card">
              <div className="w-full h-32 rounded-lg mb-4 overflow-hidden bg-gray-800">
                {product.imageUrl ? <img src={getProductImageUrl(product.imageUrl)} alt={product.name} className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} /> : null}
                <div className="w-full h-full flex items-center justify-center" style={{ display: product.imageUrl ? 'none' : 'flex' }}>
                  <Image className="w-12 h-12 text-gray-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{product.name}</h3>
              <p className="text-gray-300 text-sm mb-2">{product.description}</p>
              <div className="flex justify-between items-center">
                <span className="text-[#a15507] font-bold">${product.price}</span>
                <span className="text-gray-300 text-sm">{product.category} • {product.size}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">{t('dashboard.noProducts')}</h3>
          <p className="text-gray-400">{selectedCategory === 'all' ? t('dashboard.noProducts') : `${t('dashboard.noProducts')} ${t('dashboard.in')} ${selectedCategory} ${t('dashboard.category')}.`}</p>
          <button onClick={() => setShowAddProduct(true)} className="btn-primary mt-4">{t('dashboard.addYourFirstProduct')}</button>
        </div>
      )}
    </div>
  );
};

export default MenuTab;
